import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Archive, ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { toast } from '@/components/ui/Toast';
import { cn } from '@/lib/cn';
import { queryKeys } from '@/lib/api/queryKeys';
import { useT } from '@/lib/i18n/useT';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import { fmtInt, fmtUsd } from '@/features/insights/engine';
import { closingApi, type ClosingLine, type ClosingStatus } from './closingApi';

/**
 * El cierre del evento: cuando termina, lo que sigue en las estaciones vuelve
 * al inventario y queda un acta de cómo terminó.
 *
 * Tres estados: el evento no ha terminado (no se pinta nada); terminó y está
 * abierto (una tarjeta con lo que sobra y el botón de cerrar, que abre el
 * recuento por estación y premio); cerrado (el acta: quién, cuándo, la nota,
 * lo devuelto, lo repuesto a la marca y lo sin destino, con el detalle
 * desplegable y la opción de reabrir).
 */
export function EventClosingCard({ eventId, className }: { eventId: number; className?: string }) {
  const t = useT();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [confirmReopen, setConfirmReopen] = useState(false);
  const [showLines, setShowLines] = useState(false);

  const statusQ = useQuery({
    queryKey: queryKeys.events.closing(eventId),
    queryFn: () => closingApi.status(eventId),
    enabled: Number.isFinite(eventId) && eventId > 0,
    staleTime: 15_000,
  });

  // Todo lo que cuenta unidades cambia al cerrar: hechos de los dashboards,
  // stock y su margen, asignaciones, pool del proyecto y el propio evento.
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: queryKeys.events.closing(eventId) });
    void qc.invalidateQueries({ queryKey: ['insights'] });
    void qc.invalidateQueries({ queryKey: queryKeys.stock.all });
    void qc.invalidateQueries({ queryKey: ['stock-allocations'] });
    void qc.invalidateQueries({ queryKey: queryKeys.projectStockPool.all });
    void qc.invalidateQueries({ queryKey: queryKeys.events.all });
    void qc.invalidateQueries({ queryKey: ['eventReport'] });
  };
  const reopen = useMutation({
    mutationFn: () => closingApi.reopen(eventId),
    onSuccess: () => {
      setConfirmReopen(false);
      toast({ tone: 'success', title: t('closing.toast.reopened') });
      invalidate();
    },
    onError: (e: Error) => toast({ tone: 'error', title: e.message }),
  });

  const st = statusQ.data;
  if (!st || (!st.ended && !st.closing)) return null;

  if (st.closing) {
    const c = st.closing;
    return (
      <section className={cn('flex flex-col gap-3 rounded-[14px] border p-5', className)} style={{ background: 'var(--color-dash-paper)', borderColor: 'var(--color-dash-border)' }}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1">
            <span className="dash-mono text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: 'var(--color-dash-olive)' }}>{t('closing.kicker')}</span>
            <h3 className="text-xl font-bold" style={{ color: 'var(--color-dash-fg)' }}>
              {t('closing.reconciledOn', { date: dayjs(c.closedAt).format('MMM D, YYYY h:mm A') })}
              {c.closedBy ? ` · ${c.closedBy}` : ''}
            </h3>
            {c.note && <p className="text-sm [text-wrap:pretty]" style={{ color: 'var(--color-dash-fg-2)' }}>{c.note}</p>}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => setShowLines((v) => !v)}>
              {showLines ? <ChevronUp className="mr-1 h-4 w-4" /> : <ChevronDown className="mr-1 h-4 w-4" />}
              {t('closing.viewRecord')}
            </Button>
            <Button variant="secondary" onClick={() => setConfirmReopen(true)}>
              <RotateCcw className="mr-1 h-4 w-4" />
              {t('closing.reopen')}
            </Button>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label={t('closing.stat.returned')} value={fmtInt(c.totals.returned)} hint={t('closing.stat.returnedHint', { n: fmtInt(c.totals.restocked) })} tone="good" />
          <Stat label={t('closing.stat.delivered')} value={fmtInt(c.totals.delivered)} hint={t('closing.stat.deliveredHint', { n: fmtInt(c.totals.allocated) })} />
          <Stat label={t('closing.stat.pending')} value={fmtInt(c.totals.pending)} hint={t('closing.stat.pendingHint')} tone={c.totals.pending ? 'warn' : 'default'} />
          <Stat label={t('closing.stat.unaccounted')} value={fmtInt(c.totals.unaccounted)} hint={t('closing.stat.unaccountedHint')} tone={c.totals.unaccounted ? 'bad' : 'good'} />
        </div>
        {showLines && <LinesTable lines={st.lines} readOnly />}
        <ConfirmDialog
          open={confirmReopen}
          onOpenChange={setConfirmReopen}
          tone="danger"
          title={t('closing.reopenTitle')}
          description={t('closing.reopenBody', { n: fmtInt(c.totals.returned) })}
          confirmLabel={t('closing.reopen')}
          loading={reopen.isPending}
          onConfirm={() => reopen.mutate()}
        />
      </section>
    );
  }

  const lapsed = st.totals.expired + st.totals.voided;
  return (
    <section className={cn('flex flex-wrap items-center gap-4 rounded-[14px] border p-5', className)} style={{ background: 'var(--color-dash-orange-soft)', borderColor: 'var(--color-dash-orange-border)' }}>
      <div className="flex min-w-0 flex-1 basis-[320px] flex-col gap-1">
        <span className="dash-mono text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: 'var(--color-dash-orange)' }}>{t('closing.endedKicker')}</span>
        <h3 className="text-xl font-bold" style={{ color: 'var(--color-dash-fg)' }}>{t('closing.endedTitle', { n: fmtInt(st.totals.inPlay + lapsed) })}</h3>
        <p className="text-sm [text-wrap:pretty]" style={{ color: 'var(--color-dash-orange-ink)' }}>
          {t('closing.endedBody', { inPlay: fmtInt(st.totals.inPlay), lapsed: fmtInt(lapsed), pending: fmtInt(st.totals.pending) })}
        </p>
      </div>
      <Button variant="primary" onClick={() => setOpen(true)} disabled={st.lines.length === 0}>
        <Archive className="mr-2 h-4 w-4" />
        {t('closing.closeButton')}
      </Button>
      <CloseDialog open={open} onOpenChange={setOpen} eventId={eventId} status={st} onClosed={invalidate} />
    </section>
  );
}

function Stat({ label, value, hint, tone = 'default' }: { label: string; value: string; hint?: string; tone?: 'default' | 'good' | 'warn' | 'bad' }) {
  const color = tone === 'good' ? 'var(--color-dash-olive)' : tone === 'warn' ? 'var(--color-dash-orange)' : tone === 'bad' ? 'var(--color-dash-red)' : 'var(--color-dash-fg)';
  return (
    <div className="flex flex-col gap-0.5 rounded-xl border p-3.5" style={{ borderColor: 'var(--color-dash-border)' }}>
      <span className="dash-mono text-[10px] font-bold uppercase tracking-[0.1em]" style={{ color: 'var(--color-dash-fg-muted)' }}>{label}</span>
      <span className="text-[26px] font-bold leading-none tabular-nums" style={{ color }}>{value}</span>
      {hint && <span className="text-[11px] leading-snug" style={{ color: 'var(--color-dash-fg-subtle)' }}>{hint}</span>}
    </div>
  );
}

/**
 * El recuento. Una fila por estación y premio con lo que debería haber; el
 * operador escribe lo que cuenta y la fila dice cuánto vuelve y cuánto falta.
 */
function CloseDialog({ open, onOpenChange, eventId, status, onClosed }: { open: boolean; onOpenChange: (o: boolean) => void; eventId: number; status: ClosingStatus; onClosed: () => void }) {
  const t = useT();
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [note, setNote] = useState('');
  const lines = useMemo(
    () =>
      status.lines.map((l) => {
        const key = `${l.stationId}:${l.stockId}`;
        const counted = Math.max(0, Math.min(l.onHand, counts[key] ?? l.onHand));
        return { ...l, counted, returned: counted, unaccounted: l.onHand - counted, restocked: Math.max(0, counted - l.inPlay) };
      }),
    [status.lines, counts],
  );
  const totals = lines.reduce(
    (a, l) => ({ returned: a.returned + l.returned, unaccounted: a.unaccounted + l.unaccounted, restocked: a.restocked + l.restocked }),
    { returned: 0, unaccounted: 0, restocked: 0 },
  );
  const close = useMutation({
    mutationFn: () =>
      closingApi.close(eventId, {
        note: note.trim() || undefined,
        counts: lines.filter((l) => l.counted !== l.onHand).map((l) => ({ stationId: l.stationId, stockId: l.stockId, counted: l.counted })),
      }),
    onSuccess: () => {
      onOpenChange(false);
      toast({ tone: 'success', title: t('closing.toast.closed', { n: fmtInt(totals.returned) }) });
      onClosed();
    },
    onError: (e: Error) => toast({ tone: 'error', title: e.message }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-6xl">
        <DialogHeader>
          <DialogTitle>{t('closing.dialogTitle', { name: status.event.name })}</DialogTitle>
          <DialogDescription>{t('closing.dialogBody')}</DialogDescription>
        </DialogHeader>
        <LinesTable lines={lines} onCount={(key, v) => setCounts((c) => ({ ...c, [key]: v }))} />
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <Stat label={t('closing.stat.returned')} value={fmtInt(totals.returned)} hint={t('closing.stat.returnedHint', { n: fmtInt(totals.restocked) })} tone="good" />
          <Stat label={t('closing.stat.unaccounted')} value={fmtInt(totals.unaccounted)} hint={t('closing.stat.unaccountedHint')} tone={totals.unaccounted ? 'bad' : 'good'} />
          <Stat label={t('closing.stat.pending')} value={fmtInt(status.totals.pending)} hint={t('closing.stat.pendingHint')} tone={status.totals.pending ? 'warn' : 'default'} />
        </div>
        <label className="mt-4 block">
          <span className="mb-1.5 block text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-fg-muted)]">{t('closing.note')}</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder={t('closing.notePlaceholder')}
            className="w-full rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-input)] px-3 py-2 text-sm text-[var(--color-fg-primary)] focus:border-[var(--color-accent-ink)] focus:outline-none"
          />
        </label>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={close.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={() => close.mutate()} disabled={close.isPending}>
            {close.isPending ? t('common.saving') : t('closing.confirm', { n: fmtInt(totals.returned) })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LinesTable({ lines, readOnly, onCount }: { lines: ClosingLine[]; readOnly?: boolean; onCount?: (key: string, value: number) => void }) {
  const t = useT();
  const th = 'py-2 text-right text-[10px] font-extrabold uppercase tracking-wider text-[var(--color-fg-subtle)]';
  return (
    <div className="max-h-[50vh] overflow-auto rounded-lg border" style={{ borderColor: 'var(--color-dash-border)' }}>
      <table className="w-full min-w-[980px] text-sm">
        <thead className="sticky top-0" style={{ background: 'var(--color-dash-paper-3)' }}>
          <tr>
            <th className={cn(th, 'pl-3 text-left')}>{t('closing.col.station')}</th>
            <th className={cn(th, 'text-left')}>{t('closing.col.stock')}</th>
            <th className={th}>{t('closing.col.allocated')}</th>
            <th className={th}>{t('closing.col.delivered')}</th>
            <th className={th}>{t('closing.col.pending')}</th>
            <th className={th}>{t('closing.col.lapsed')}</th>
            <th className={th}>{t('closing.col.inPlay')}</th>
            <th className={th}>{t('closing.col.onHand')}</th>
            <th className={th}>{t('closing.col.counted')}</th>
            <th className={th}>{t('closing.col.returned')}</th>
            <th className={cn(th, 'pr-3')}>{t('closing.col.unaccounted')}</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => {
            const key = `${l.stationId}:${l.stockId}`;
            return (
              <tr key={key} className="border-t" style={{ borderColor: 'var(--color-dash-track)' }}>
                <td className="py-2 pl-3 font-semibold">{l.stationName}</td>
                <td className="py-2">
                  <span className="flex items-center gap-2">
                    {l.stockImage && <img src={resolveImageUrl(l.stockImage)} alt="" className="h-6 w-6 rounded object-cover" />}
                    <span className="truncate">{l.stockName}</span>
                    {l.unitCost != null && <span className="dash-mono text-[10px]" style={{ color: 'var(--color-dash-fg-subtle)' }}>{fmtUsd(l.unitCost)}</span>}
                  </span>
                </td>
                <td className="py-2 text-right tabular-nums">{fmtInt(l.allocated)}</td>
                <td className="py-2 text-right tabular-nums">{fmtInt(l.delivered)}</td>
                <td className="py-2 text-right tabular-nums" style={{ color: l.pending ? 'var(--color-dash-orange)' : undefined }}>{fmtInt(l.pending)}</td>
                <td className="py-2 text-right tabular-nums">{fmtInt(l.expired + l.voided)}</td>
                <td className="py-2 text-right tabular-nums">{fmtInt(l.inPlay)}</td>
                <td className="py-2 text-right font-semibold tabular-nums">{fmtInt(l.onHand)}</td>
                <td className="py-2 text-right tabular-nums">
                  {readOnly || !onCount ? (
                    fmtInt(l.counted)
                  ) : (
                    <input
                      type="number"
                      min={0}
                      max={l.onHand}
                      value={l.counted}
                      onChange={(e) => onCount(key, Number(e.target.value))}
                      className="w-20 rounded-md border px-2 py-1 text-right text-sm tabular-nums focus:outline-none"
                      style={{ borderColor: l.counted < l.onHand ? 'var(--color-dash-red)' : 'var(--color-dash-border-2)', background: 'var(--color-bg-input)' }}
                    />
                  )}
                </td>
                <td className="py-2 text-right font-semibold tabular-nums" style={{ color: 'var(--color-dash-olive)' }}>{fmtInt(l.returned)}</td>
                <td className="py-2 pr-3 text-right font-semibold tabular-nums" style={{ color: l.unaccounted ? 'var(--color-dash-red)' : 'var(--color-dash-fg-subtle)' }}>{fmtInt(l.unaccounted)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
